import { SegmentNamesByProductCode } from '@upright/common/model/input/companies/products/resolve-blanket-product-segments';
import { ByProductCode } from '@upright/common/model/input/products/types';
import { CompanyLikeEntityType, ModelEntityType } from '@upright/common/model/input/types';
import { responseSchemaFor } from '@upright/common/zod-utils';
import { z } from 'zod';
import { CsrdVersionSchema, MaterialityValence } from '@upright/common/model/input/materiality-assessment/types';
import { SustainabilityMatterId } from '@upright/common/model/input/materiality-assessment/types-matters';
import { WarmingTier } from '@upright/common/model/output/materiality/csrd-company-results/climate/warming-tier';
import { ExampleEntityIds } from '../../scores/hard-coded-entity-ids';
import { CsrdFinancialEffectsParameters } from './financial-effects/types';
import {
  FinancialMaterialityResult,
  ImpactMaterialityResult,
  MaterialityResultOfMatter,
  MinimalMaterialityResultForMatter,
} from './types';
import { CsrdResultPartType, MaterialityResultPartsByType } from './types-result-parts';

import { ImpactMaterialityLowLevelResult } from '@upright/common/model/output/materiality/csrd-company-results/types';

export const zTriggerOnDemandMaterialityJobRequest = z.object({
  variantId: z.string(),
  draft: z.boolean(),
});
export type TriggerOnDemandMaterialityJobRequest = z.infer<typeof zTriggerOnDemandMaterialityJobRequest>;
export type TriggerOnDemandMaterialityJobResponse = { jobId: number };
export type OndemandModeParams = {
  jobId?: number;
};

export type GetCsrdCompanyResultsRequestPathParams = {
  variantId: string;
};
export type GetCsrdCompanyResultOfMatterRequestPathParams = {
  variantId: string;
  matterId: SustainabilityMatterId;
};
export type GetCsrdCompanyResultsRequestQueryParams = OndemandModeParams;
export type GetCsrdCompanyResultsResponse = MaterialityResultOfMatter[];
export type GetCsrdCompanyResultOfMatterResponse = MaterialityResultOfMatter;

export type GetCompanyCsrdVersionSchemaResponse = {
  versionSchema: CsrdVersionSchema;
};

export type GetCsrdCompanyResultPartsRequestPathParams = {
  variantId: string;
};
export type GetCsrdCompanyResultPartsRequestQueryParams<T extends CsrdResultPartType = CsrdResultPartType> = {
  partTypes: T[];
  matterId?: SustainabilityMatterId;
  valence?: MaterialityValence | null;
} & OndemandModeParams;

export type GetCsrdCompanyResultPartsResponse<T extends CsrdResultPartType = CsrdResultPartType> = Pick<
  MaterialityResultPartsByType,
  T
>;

export type GetCsrdCompanyProductSegmentsRequestPathParams = {
  variantId: string;
};

/**
 * Business unit / revenue segment labels for the variant's published products, resolved at read
 * time from the blanket set behind the product set. Empty for a company whose product set has no
 * blanket behind it or whose blanket carries no segments, and empty for a caller who would see
 * product-level results masked — a segment name is product-set information.
 */
export type GetCsrdCompanyProductSegmentsResponse = {
  segmentNamesByProductCode: SegmentNamesByProductCode;
};

export interface GetStatementCountsRequestPathParams {
  variantId: string;
}

export interface ImpactStatementCount {
  giseRunId: number;
  impactorName: string;
  impactTopicText: string;
  filteredArticleCount: number;
  psCount: number;
}

export interface GetReasoningsVisibilityRequestPathParams {
  entityId: string;
}

export interface GetReasoningsVisibilityResponse {
  reasoningsVisibleForUser: boolean;
  reasoningsOfEntityVisible: boolean;
}

export type UpdateReasoningsVisibilityRequestPayload = {
  reasoningsVisible: boolean;
};

export type GetStatementCountsResponse = ImpactStatementCount[];

export type BenchmarkDetails = {
  entityId: string;
  entityType: CompanyLikeEntityType;
  benchmarkName: string;
};

type ByImpactTopicId<T> = { [k: string]: T };
export type GetProductEvidenceResponse = ByProductCode<ByImpactTopicId<boolean>>;

// TODO: Get the benchmark from db on an entity-basis
export const csrdFinaFxBenchmarkDetails: BenchmarkDetails = {
  entityId: ExampleEntityIds.EURO_STOXX_50_EXCLUDING_FINANCIAL_INSTITUTIONS,
  entityType: ModelEntityType.GROUP,
  benchmarkName: 'Euro Stoxx 50 (excluding financial institutions)',
};

export type GetMinimalMaterialityResultForLeafMattersResponse = {
  financialEffectsParameters: CsrdFinancialEffectsParameters | undefined;
  materialities: MinimalMaterialityResultForMatter[];
};

// `materialIroCount` only exists from CsrdVersionSchema 3 onwards, so results computed against
// an older schema carry no count at all.
const zMaterialIroCount = z.number().nullish();

const zMinimalImpactMaterialityResultDatum = z.object({
  severityLevel: z.string(),
  severityScore: z.number(),
  valence: z.string(),
  isMaterial: z.boolean(),
  materialityType: z.string(),
  materialIroCount: zMaterialIroCount,
});

const zMinimalFinancialMaterialityResultDatum = z.object({
  materialityLevel: z.string(),
  materialityScore: z.number(),
  valence: z.string(),
  isMaterial: z.boolean(),
  materialityType: z.string(),
  materialIroCount: zMaterialIroCount,
});

const zMinimalFinancialEffectResultDatum = z.object({
  delta: z.number(),
  effect: z.number(),
  materialityLevel: z.string(),
  valence: z.string(),
  isMaterial: z.boolean(),
  materialIroCount: zMaterialIroCount,
});

const zMinimalMaterialityResultForMatter = z.object({
  matterId: z.string(),
  imResult: z.string(),
  fmResult: z.string(),
  companyImpactMateriality: z.array(zMinimalImpactMaterialityResultDatum),
  companyFinancialMateriality: z.array(zMinimalFinancialMaterialityResultDatum),
  companyFinancialEffects: z.array(zMinimalFinancialEffectResultDatum),
  isMaterial: z.boolean(),
});

/**
 * Response guard for the minimal DMA results endpoint, checked at the fetch boundary (see
 * `fetchAndValidateOrThrow`).
 *
 * This one payload carries the whole double materiality assessment the customer reports on: the
 * matrix position, the materiality verdict and the euro-denominated financial effect of every
 * matter. Each datum is a projection (`Pick`) of a database row, so a column rename or a numeric
 * column arriving as a string reaches the UI as a matter drawn at the wrong place on the matrix,
 * or as an effect of `0` — an assertion nobody can tell apart from a computed one.
 *
 * Matter ids and the level/valence codes are validated as strings, not as their enums, so that a
 * new ESRS matter or a new level in a later CSRD version schema does not fail the response.
 */
const zGetMinimalMaterialityResultForLeafMattersResponseSchema = z.object({
  financialEffectsParameters: z
    .object({
      comparisonItemId: z.string(),
      assessmentYears: z.number(),
      financialStatementCurrency: z.string().nullable(),
    })
    .optional(),
  materialities: z.array(zMinimalMaterialityResultForMatter),
});
export const zGetMinimalMaterialityResultForLeafMattersResponse = responseSchemaFor<
  GetMinimalMaterialityResultForLeafMattersResponse,
  typeof zGetMinimalMaterialityResultForLeafMattersResponseSchema
>(zGetMinimalMaterialityResultForLeafMattersResponseSchema);

export type GetCompanyImpactsRequestPathParams = {
  variantId: string;
  matterId: SustainabilityMatterId;
  valence: MaterialityValence;
};

export type GetCompanyImpactsRequestQueryParams = OndemandModeParams;

// A type for impacts formed from low level parts are expected to be deduplicated so that each part to constitute to one impact
export type GetCompanyImpactsResponse = ImpactMaterialityLowLevelResult[];

// A type for impacts where low level parts are not deduplicated but instead one impact consists of multiple low level
// parts grouped into one impact.
export type GetCompanyGroupedImpactsResponse = ImpactMaterialityResult[];

export type GetCompanyRisksOrOpportunitiesRequestPathParams = {
  variantId: string;
  matterId: SustainabilityMatterId;
  valence: MaterialityValence;
};

export type GetCompanyRisksOrOpportunitiesRequestQueryParams = OndemandModeParams & {
  includeProductContributions?: boolean;
  /**
   * Warming tier for per-scenario product contributions. When set (with
   * `includeProductContributions`), each product's probability / magnitude /
   * contribution are taken from the trigger's per-scenario values for this tier
   * (PLT-5681), plus that scenario's probability / magnitude reasoning for the
   * transition view's tooltips (PLT-5677). Absent → base (scenario-independent)
   * values, no reasoning.
   */
  tier?: WarmingTier;
};

export type GetCompanyRisksOrOpportunitiesResponse = FinancialMaterialityResult[];

/** PLT-7940: the staff-only read of every matter's and valence's rows at once. */
export type GetAllCompanyRisksOrOpportunitiesRequestPathParams = {
  variantId: string;
};

/**
 * Product contributions are always included. With `tier`, only the E1 climate
 * matters are returned, traced at that tier; without it, every matter at base values.
 */
export type GetAllCompanyRisksOrOpportunitiesRequestQueryParams = OndemandModeParams & {
  tier?: WarmingTier;
};

export type GetMaterialityVariantsResponse = {
  originalVariantId: string;
  tailoringVariantId: string | undefined;
  isFrozen: {
    originalVariant: boolean;
    tailoringVariant?: boolean;
  };
};

export type GetMaterialityVariantsRequestPathParams = {
  companyId: string;
  release: string;
};

export type GetMaterialityVariantsRequestQueryParams = {
  scenarioId?: string;
  startDate?: string;
  endDate?: string;
};
